/**
 * InlineTransactionRow — Single transaction row with colored category icon
 *
 * Shows merchant name, AttributionBadge, date, recurring/review/manual icons,
 * linked artwork title, Link pill (income only), and amount.
 *
 * Extracted from ActivityZones for reuse in ActivityCard and InlineReviewCard.
 */

import { T } from '@/styles/tokens';
import { AttributionBadge } from '@/components/money/AttributionBadge';
import { MerchantAvatar } from '@/components/money/MerchantAvatar';
import type { ArtTransaction } from '@/hooks/useArtTransactions';
import { formatCurrency } from '@/lib/formatCurrency';

function formatAmount(cents: number, category: string): string {
  const formatted = formatCurrency(cents, 'transaction');
  if (category === 'ART_INCOME') return `+${formatted}`;
  return `\u2212${formatted}`; // − (minus sign) for expenses
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function InlineTransactionRow({
  txn,
  isIncome,
  category,
  linkedTitle,
  onLinkTap,
  onTap,
  showDivider,
  showTag,
}: {
  txn: ArtTransaction;
  isIncome: boolean;
  category: string;
  linkedTitle?: string | null;
  onLinkTap?: () => void;
  onTap?: () => void;
  showDivider: boolean;
  /** Optional tag shown next to merchant name (e.g. category label for review cards) */
  showTag?: string | null;
}) {
  return (
    <>
      {showDivider && (
        <div style={{ height: 1, backgroundColor: 'rgba(0,0,0,0.04)', margin: '0 16px' }} />
      )}
      <div
        onClick={(e) => { e.stopPropagation(); onTap?.(); }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '9px 16px 9px 20px',
          cursor: 'pointer',
          backgroundColor: T.sand,
        }}>
        {/* Row icon — colored circle */}
        <MerchantAvatar merchantName={txn.merchantName} logoUrl={txn.merchantLogoUrl} category={txn.tpCategory} size={28} />

        {/* Merchant name + badge + date */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              fontSize: 13,
              fontWeight: 500,
              color: T.text,
              whiteSpace: 'nowrap' as const,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2,
            }}>
              {txn.merchantName}
            </span>
            <AttributionBadge confirmedBy={txn.tpConfirmedBy} />
            {showTag && (
              <span style={{
                fontSize: 9,
                fontWeight: 600,
                color: T.gray400,
                backgroundColor: 'rgba(0,0,0,0.04)',
                padding: '1px 5px',
                borderRadius: 4,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
              }}>
                {showTag}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.gray400, marginTop: 2, lineHeight: 1.2 }}>
            <span>{formatShortDate(txn.date)}</span>
            {txn.isRecurring && (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                <path d="M11.5 5.5A4.5 4.5 0 002.5 7M2.5 8.5A4.5 4.5 0 0011.5 7" stroke={T.gray400} strokeWidth="1.3" strokeLinecap="round"/>
                <path d="M9.5 5.5H11.5V3.5" stroke={T.gray400} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4.5 8.5H2.5V10.5" stroke={T.gray400} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            {txn.tpCategory === 'UNCERTAIN' && (
              <span style={{ fontSize: 10, color: T.warning, fontWeight: 600 }}>Review</span>
            )}
            {txn.source === 'manual' && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                <path d="M8.5 1.5L10.5 3.5L4 10L1 11L2 8L8.5 1.5Z" stroke={T.gray400} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            {isIncome && linkedTitle && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 10, color: T.primary, fontWeight: 600 }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M6 8L8 6M5 9.5L3.5 11A1.5 1.5 0 005 12.5L6.5 11M8 4.5L9.5 3A1.5 1.5 0 0111 4.5L9.5 6" stroke={T.primary} strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
                {linkedTitle}
              </span>
            )}
          </div>
        </div>

        {/* Match pill (income only, unlinked) — action to match deposit to artwork */}
        {isIncome && !txn.tpLinkedArtworkId && (
          <button
            onClick={(e) => { e.stopPropagation(); onLinkTap?.(); }}
            style={{
              padding: '2px 8px',
              borderRadius: 26,
              background: 'transparent',
              border: '1.5px dashed #D4D1CC',
              fontSize: 10,
              fontWeight: 600,
              color: '#BCBCBC',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              flexShrink: 0,
              fontFamily: T.fontFamily,
              textTransform: 'uppercase' as const,
            }}
          >
            Match
          </button>
        )}

        {/* Amount — teal when linked, grey when unlinked */}
        <span style={{
          fontSize: 14,
          fontWeight: 600,
          color: isIncome ? (txn.tpLinkedArtworkId ? T.primary : '#BCBCBC') : T.text,
          flexShrink: 0,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.3px',
        }}>
          {formatAmount(txn.amount, txn.tpCategory)}
        </span>
      </div>
    </>
  );
}
